import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import ImageInput from 'components/field/ImageInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useMediaQuery from 'hooks/useMediaQuery';

import { ENUMS, SMS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';

/**
 * @author @muhammedburakak
 */

const ContentPreferences = () => {
	const axios = useAxiosPrivate();
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const { showSuccess, showError } = useAlert();

	const [loading, setLoading] = useState(true);

	const schema = useMemo(
		() =>
			yup.object().shape({
				advImage: yup.string().nullable(),
				captionText: yup.string().nullable().required(),
				advertisementLink: yup.string().nullable(),
				showOnScreen: yup.string().nullable().required(),
				languageType: yup.string().nullable().required()
			}),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			ads: []
		}
	});

	const { control, setValue, reset, watch, setError, clearErrors } = formMethods;

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'contentPreferences'
	});

	const getContentPreferences = useCallback(async () => {
		try {
			const response = await axios.post(SMS.GET_CONTENT_PREFERENCES);

			if (response.status === 200) {
				reset({
					contentPreferences: response.data
				});
				setLoading(false);
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
		}
	}, []);

	useEffect(() => {
		getContentPreferences();
	}, []);

	const editItem = async ({ index }) => {
		let isSuccess = false;
		const fieldName = `contentPreferences[${index}]`;
		clearErrors(fieldName);

		try {
			const row = watch(fieldName);
			schema.validateSync(row, {
				abortEarly: false
			});

			isSuccess = true;
			const isUpdateRequest = !!row.ID;

			try {
				const response = await axios.post(
					isUpdateRequest ? SMS.UPDATE_CONTENT_PREFERENCES : SMS.ADD_CONTENT_PREFERENCES,
					row
				);
				if (response.status === 200) {
					showSuccess();

					if (!isUpdateRequest) {
						setValue(`${fieldName}.ID`, response.data.ID);
					}
				} else {
					showError();
					AppLogger.error(response);
				}
			} catch (error) {
				AppLogger.error(error);
				showError();
			}
		} catch (error) {
			error.inner.forEach((error) => {
				setError(`${fieldName}.${error.path}`, {
					type: error.type,
					message: error.message
				});
			});
		}

		return isSuccess;
	};

	const removeItem = async ({ index, original: { ID } }) => {
		if (ID) {
			try {
				const response = await axios.post(SMS.DELETE_CONTENT_PREFERENCES, {
					ID
				});
				if (response.status === 200) {
					showSuccess();
					remove(index);
				} else {
					AppLogger.error(response);
				}
			} catch (error) {
				AppLogger.error(error);
				showError();
			}
		} else {
			remove(index);
		}
	};

	const data = fields;
	const columns = useMemo(
		() => [
			{
				header: 'image',
				accessorKey: 'advImage',
				size: isMobile ? 50 : 100,
				renderCell: ({ row: { index } }) => (
					<ImageInput
						name={`contentPreferences[${index}].advImage`}
						value={watch(`contentPreferences[${index}].advImage`)}
						setValue={(value) => {
							setValue(`contentPreferences[${index}].advImage`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
						viewMode
					/>
				),
				editCell: ({ row: { index } }) => (
					<ImageInput
						name={`contentPreferences[${index}].advImage`}
						value={watch(`contentPreferences[${index}].advImage`) ?? null}
						setValue={(value) => {
							setValue(`contentPreferences[${index}].advImage`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
					/>
				)
			},
			{
				header: 'captionText',
				accessorKey: 'captionText',
				required: true,
				size: 100,
				showOnMobile: true,
				renderCell: ({ row: { index } }) => (
					<StringInput name={`contentPreferences[${index}].captionText`} viewMode />
				),
				editCell: ({ row: { index } }) => <StringInput name={`contentPreferences[${index}].captionText`} />
			},

			{
				header: 'onClickLink',
				accessorKey: 'advertisementLink',
				size: 100,
				renderCell: ({ row: { index } }) => (
					<StringInput name={`contentPreferences[${index}].advertisementLink`} viewMode />
				),
				editCell: ({ row: { index } }) => (
					<StringInput name={`contentPreferences[${index}].advertisementLink`} />
				)
			},
			{
				header: 'showOn',
				accessorKey: 'showOnScreen',
				required: true,
				size: 150,
				renderCell: ({ row: { index } }) => (
					<EnumSelect
						placeholder="selectOne"
						setValue={(value) => {
							setValue(`contentPreferences[${index}].showOnScreen`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
						enumName={ENUMS.V2_SHOW_ON_SCREEN}
						name={`contentPreferences[${index}].showOnScreen`}
						value={watch(`contentPreferences[${index}].showOnScreen`)}
						searchable={false}
						viewMode
					/>
				),
				editCell: ({ row: { index } }) => (
					<EnumSelect
						placeholder="selectOne"
						setValue={(value) => {
							setValue(`contentPreferences[${index}].showOnScreen`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
						enumName={ENUMS.V2_SHOW_ON_SCREEN}
						name={`contentPreferences[${index}].showOnScreen`}
						value={watch(`contentPreferences[${index}].showOnScreen`)}
						searchable={false}
					/>
				)
			},

			{
				header: 'language',
				accessorKey: 'languageType',
				required: true,
				size: 150,
				renderCell: ({ row: { index } }) => (
					<EnumSelect
						placeholder="selectOne"
						setValue={(value) => {
							setValue(`contentPreferences[${index}].languageType`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
						enumName={ENUMS.LANGUAGE_CODES}
						name={`contentPreferences[${index}].languageType`}
						value={watch(`contentPreferences[${index}].languageType`)}
						searchable={false}
						viewMode
					/>
				),
				editCell: ({ row: { index } }) => (
					<EnumSelect
						placeholder="selectOne"
						setValue={(value) => {
							setValue(`contentPreferences[${index}].languageType`, value, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
						enumName={ENUMS.LANGUAGE_CODES}
						name={`contentPreferences[${index}].languageType`}
						value={watch(`contentPreferences[${index}].languageType`)}
						searchable={false}
					/>
				)
			},
			{
				id: 'editRow',
				size: isMobile ? 10 : 30,
				maxSize: isMobile ? 10 : 30,
				showOnMobile: true
			},
			{
				id: 'deleteRow',
				size: isMobile ? 10 : 30,
				maxSize: isMobile ? 10 : 30,
				showOnMobile: true
			}
		],
		[]
	);

	return (
		<Form formMethods={formMethods} borderless>
			<Table
				data={data}
				columns={columns}
				onRowEditEnd={editItem}
				onRowDelete={(row) => removeItem(row)}
				onRowAdd={() => {
					append({
						advImage: null,
						captionText: null,
						advertisementLink: null,
						showOnScreen: null,
						languageType: null
					});
				}}
				showAddNewRowButton
				deletableLastRow
				loading={loading}
			/>
		</Form>
	);
};

export default NiceModal.create(() => {
	const modal = useModal();
	return (
		<Modal fullwidth maxWidth="x-large" className="modal-contentPreferences">
			<ModalTitle>
				<Typography variant="p1">contentPreferences</Typography>
			</ModalTitle>

			<ModalContent>
				<ContentPreferences />
			</ModalContent>

			<ModalActions>
				<Button
					label="close"
					variant="outline"
					onClick={() => {
						modal.remove();
					}}
				/>
			</ModalActions>
		</Modal>
	);
});
